import { Component, OnInit, Inject, ChangeDetectorRef } from '@angular/core';
import { MdDialogRef, MD_DIALOG_DATA } from '@angular/material';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs/Observable';
import { ObservableRequestService } from 'app/services/observable-request.service';
import { ConstantsService } from 'app/services/constants-service';
import { FinanceService } from 'app/finance/finance.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { WarmUpService, ApplicationTypeConstants } from 'app/services/warmup/warmup.service';
import { MessageService } from 'app/services/messages.service';
import { GenericSearchDetails } from 'app/shared/models/generic-search-details.model';
import { PEPMatchResolution } from 'app/entities/monitoring/actual/pep-match.model';
import { SanctionMatchResolution } from 'app/entities/monitoring/actual/sanction-matches.model';
import { BadPressMatchResolution } from 'app/entities/monitoring/actual/badpress-match.model';
import { Headers } from '@angular/http';

interface BankAccount {
  accountId: number;
  accountName: string;
  accountNumber: string;
  bankName?: string;
  currencyValue?: string;
  currency?: string;
}

@Component({
  selector: 'app-find-account-dialog',
  templateUrl: './find-account-dialog.component.html',
  styleUrls: ['./find-account-dialog.component.css']
})
export class FindAccountDialogComponent implements OnInit {
  // Combined view - no steps, show both client and account at once
  step: 'client' | 'account' = 'client'; // Keep for backward compatibility but not used in UI
  
  // Client selection
  clientSearchForm: FormGroup;
  selectedClient: GenericSearchDetails = null;
  clientSearchURI = ConstantsService.CLIENT_SEARCH;
  clientSearchPlaceholder = MessageService.CLIENT_NAME_SEARCH_PLACEHOLDER;
  clientSearchResults: GenericSearchDetails[] = [];
  bestMatchClient: GenericSearchDetails = null;
  searchingClients = false;
  
  // Account selection
  accountSearchForm: FormGroup;
  clientAccounts: BankAccount[] = [];
  allAccounts: BankAccount[] = []; // All accounts from search (not filtered by client)
  selectedAccount: BankAccount = null;
  matchedAccount: BankAccount = null;
  matchedBankAccountId: number = null; // Store bank account ID when found by account number
  loadingAccounts = false;
  searchingAccounts = false;
  showCreateAccountForm = false;
  accountsFoundByNumber: boolean = false; // Track if accounts were found by account number search
  accountOwners: any[] = []; // Clients that own the selected account
  accountManuallySelected = false; // User explicitly picked an account — don't auto-override
  
  // Create account form
  createAccountForm: FormGroup;
  creatingAccount = false;
  bankInstitutions: any[] = [];
  currencies: any[] = [];
  
  // Bank statement data
  extractedAccountOwner: string;
  extractedAccountNumber: string;
  extractedCurrency: string;
  bankStatementId: number;

  constructor(
    public dialogRef: MdDialogRef<FindAccountDialogComponent>,
    @Inject(MD_DIALOG_DATA) public data: any,
    private formBuilder: FormBuilder,
    private requestService: ObservableRequestService,
    private financeService: FinanceService,
    private snackBarService: SnackBarService,
    private warmUpService: WarmUpService,
    private changeDetectorRef: ChangeDetectorRef
  ) {
    // Initialize bank statement data
    this.extractedAccountOwner = (data && data.extractedAccountOwner) ? data.extractedAccountOwner : '';
    this.extractedAccountNumber = (data && data.extractedAccountNumber) ? data.extractedAccountNumber : '';
    this.extractedCurrency = (data && data.extractedCurrency) ? data.extractedCurrency : '';
    this.bankStatementId = (data && data.bankStatementId) ? data.bankStatementId : null;
    
    // Client search form
    this.clientSearchForm = this.formBuilder.group({
      clientSearch: [this.extractedAccountOwner || '', Validators.required]
    });
    
    // Account search form
    this.accountSearchForm = this.formBuilder.group({
      accountSearch: [this.extractedAccountNumber || '', Validators.required]
    });
    
    // Create account form
    this.createAccountForm = this.formBuilder.group({
      accountName: ['', Validators.required],
      accountNumber: [{value: this.extractedAccountNumber || '', disabled: true}, Validators.required],
      bankInstitutionId: ['', Validators.required],
      currencyId: ['', Validators.required]
    });
  }

  ngOnInit() {
    // If we have account number, search by account number first (this will find both clients and accounts)
    if (this.extractedAccountNumber && this.extractedAccountNumber.trim()) {
      // Search by account number - this finds clients that own the account
      this.searchByAccountNumber();
    }
    
    // If we have account owner, pre-search for clients
    if (this.extractedAccountOwner && this.extractedAccountOwner.trim()) {
      this.searchClients(this.extractedAccountOwner);
    }
    
    // Load bank institutions and currencies for create account form
    this.loadBankInstitutions();
    this.loadCurrencies();
  }

  searchByAccountNumber() {
    if (!this.extractedAccountNumber || !this.extractedAccountNumber.trim()) {
      return;
    }
    
    console.log('searchByAccountNumber called with account number:', this.extractedAccountNumber);
    this.searchingClients = true;
    
    const payload = {
      accountNumber: this.extractedAccountNumber.trim()
    };
    
    this.requestService.postRequest(
      ConstantsService.BANK_STATEMENT_BATCH_FIND_ACCOUNT_HOLDERS,
      payload,
      new Headers()
    ).subscribe(
      (response: any) => {
        this.searchingClients = false;
        console.log('searchByAccountNumber response:', response);
        
        if (response && response.holders && Array.isArray(response.holders)) {
          const holders = response.holders;
          console.log('searchByAccountNumber found', holders.length, 'holders');
          
          // Convert holders to GenericSearchDetails format
          this.clientSearchResults = holders.map((holder: any) => {
            const clientDetails = new GenericSearchDetails();
            clientDetails.id = holder.entityId;
            clientDetails.clientId = holder.clientId;
            clientDetails.entityId = holder.entityId;
            clientDetails.clientName = holder.clientName || 'Unknown';
            clientDetails.clientType = holder.clientType || 'Unknown';
            clientDetails.clientGender = null;
            clientDetails.clientLogo = null;
            clientDetails.entityType = holder.clientType || 'Unknown';
            // Set default values for status fields
            clientDetails.sanctionStatus = SanctionMatchResolution.FALSE;
            clientDetails.pepStatus = PEPMatchResolution.FALSE;
            clientDetails.badPressStatus = BadPressMatchResolution.FALSE;
            return clientDetails;
          });
          
          // Store bank account ID when unambiguous (by number alone, or by number + statement currency)
          const uniqueBankAccountIds = new Set<number>();
          holders.forEach((holder: any) => {
            if (holder.bankAccountId) {
              uniqueBankAccountIds.add(holder.bankAccountId);
            }
          });
          if (uniqueBankAccountIds.size === 1) {
            this.matchedBankAccountId = holders[0].bankAccountId;
            this.accountsFoundByNumber = true;
          } else if (uniqueBankAccountIds.size > 1) {
            this.accountsFoundByNumber = true;
            this.allAccounts = this.buildAccountsFromHolders(holders, response.accountNumber);
            const currencyMatch = this.pickAccountByCurrency(this.allAccounts);
            this.matchedBankAccountId = currencyMatch ? currencyMatch.accountId : null;
          }
          
          if (holders.length === 1) {
            const client = this.clientSearchResults[0];
            if (client && client.clientId && client.entityId) {
              if (this.matchedBankAccountId && !this.selectedAccount && !this.accountManuallySelected) {
                const holder = holders.find(h => h.bankAccountId === this.matchedBankAccountId) || holders[0];
                this.selectedAccount = this.buildAccountFromHolder(holder, response.accountNumber);
                this.matchedAccount = this.selectedAccount;
              }
              this.selectClient(client, false);
            }
          } else if (holders.length > 1) {
            this.bestMatchClient = this.clientSearchResults[0];
            if (this.matchedBankAccountId && !this.selectedAccount && !this.accountManuallySelected) {
              const holder = holders.find(h => h.bankAccountId === this.matchedBankAccountId);
              if (holder) {
                this.selectedAccount = this.buildAccountFromHolder(holder, response.accountNumber);
                this.matchedAccount = this.selectedAccount;
              }
            }
          } else {
            this.clientSearchResults = [];
            this.bestMatchClient = null;
            this.selectedClient = null;
          }
        } else {
          this.clientSearchResults = [];
          this.bestMatchClient = null;
        }
      },
      (error: any) => {
        this.searchingClients = false;
        console.error('Error searching by account number: ' + error.message);
        this.snackBarService.showSimpleMsj('Error searching by account number: ' + (error.message || 'Unknown error'));
      }
    );
  }

  searchClients(searchTerm: string) {
    if (!searchTerm || searchTerm.trim().length < 2) {
      // Clearing or shortening the search only hides results — keep the selected client and
      // their loaded accounts visible so the user can still pick an account.
      this.clientSearchResults = [];
      this.bestMatchClient = this.selectedClient;
      return;
    }
    
    this.searchingClients = true;
    
    // Use the same endpoint format as app-header-search component (path parameter)
    const searchQuery = encodeURIComponent(searchTerm.trim());
    this.requestService.getRequest(
      `${ConstantsService.CLIENT_SEARCH}/${searchQuery}`
    ).subscribe(
      (response: any) => {
        this.searchingClients = false;
        
        // Parse response - format is: { flagResult: true, arrResult: { organisations: [...], persons: [] } }
        this.clientSearchResults = [];
        
        if (response && response.arrResult) {
          // Parse organisations (clients)
          const organisations = response.arrResult.organisations || [];
          const persons = response.arrResult.persons || [];
          
          // Convert organisations to GenericSearchDetails format
          for (const org of organisations) {
            const clientDetails = new GenericSearchDetails();
            clientDetails.id = org.id;
            clientDetails.clientId = org.clientId;
            clientDetails.entityId = org.entityId;
            clientDetails.clientName = org.name;
            clientDetails.clientType = 'Organisation';
            clientDetails.clientGender = null;
            clientDetails.clientLogo = org.logo || null;
            clientDetails.entityType = 'Organisation';
            clientDetails.sanctionStatus = this.convertToSanctionMatchResolution(org.sanctionStatus);
            clientDetails.pepStatus = PEPMatchResolution.FALSE; // Organisations typically don't have PEP status
            clientDetails.badPressStatus = this.convertToBadPressMatchResolution(org.badPressStatus);
            this.clientSearchResults.push(clientDetails);
          }
          
          // Convert persons to GenericSearchDetails format (if any)
          for (const person of persons) {
            const personName = this.parsePersonName(person);
            const clientDetails = new GenericSearchDetails();
            clientDetails.id = person.id;
            clientDetails.clientId = person.clientId;
            clientDetails.entityId = person.entityId;
            clientDetails.clientName = personName;
            clientDetails.clientType = 'Person';
            clientDetails.clientGender = person.genderName || null;
            clientDetails.clientLogo = person.logo || null;
            clientDetails.entityType = 'Person';
            clientDetails.sanctionStatus = this.convertToSanctionMatchResolution(person.sanctionStatus);
            clientDetails.pepStatus = this.convertToPEPMatchResolution(person.pepStatus);
            clientDetails.badPressStatus = this.convertToBadPressMatchResolution(person.badPressStatus);
            this.clientSearchResults.push(clientDetails);
          }
        }
        
        // Find best match and auto-select only when unambiguous
        if (this.clientSearchResults.length > 0) {
          const ownerName = (this.extractedAccountOwner || '').trim().toLowerCase();
          const exactMatch = ownerName
            ? this.clientSearchResults.find(
                client => client.clientName && client.clientName.toLowerCase() === ownerName
              )
            : null;

          if (exactMatch) {
            this.selectClient(exactMatch, false);
          } else if (this.clientSearchResults.length === 1) {
            this.selectClient(this.clientSearchResults[0], false);
          } else {
            this.bestMatchClient = this.clientSearchResults[0];
            if (this.selectedClient
                && !this.clientSearchResults.some(c => c.clientId === this.selectedClient.clientId)) {
              this.selectedClient = null;
              this.clientAccounts = [];
              if (!this.accountManuallySelected) {
                this.selectedAccount = null;
                this.matchedAccount = null;
              }
            }
          }
        } else {
          this.bestMatchClient = null;
        }
      },
      (error: any) => {
        this.searchingClients = false;
        console.error('Error searching clients: ' + error.message);
        this.snackBarService.showSimpleMsj('Error searching clients: ' + (error.message || 'Unknown error'));
      }
    );
  }

  onClientSearchChange(value: string) {
    this.searchClients(value);
  }

  parsePersonName(person: any): string {
    if (!person) {
      return '';
    }
    
    // Build person name from available fields
    const parts: string[] = [];
    
    if (person.firstNames) {
      parts.push(person.firstNames);
    }
    if (person.middleNames) {
      parts.push(person.middleNames);
    }
    if (person.lastNames) {
      parts.push(person.lastNames);
    }
    
    if (parts.length > 0) {
      return parts.join(' ');
    }
    
    // Fallback to name field if available
    if (person.name) {
      return person.name;
    }
    
    return '';
  }

  convertToSanctionMatchResolution(value: string): SanctionMatchResolution {
    if (!value) {
      return SanctionMatchResolution.FALSE;
    }
    const upperValue = value.toUpperCase();
    if (upperValue === 'TRUE') {
      return SanctionMatchResolution.TRUE;
    } else if (upperValue === 'PROBABLE') {
      return SanctionMatchResolution.PROBABLE;
    }
    return SanctionMatchResolution.FALSE;
  }

  convertToPEPMatchResolution(value: string): PEPMatchResolution {
    if (!value) {
      return PEPMatchResolution.FALSE;
    }
    const upperValue = value.toUpperCase();
    if (upperValue === 'TRUE') {
      return PEPMatchResolution.TRUE;
    } else if (upperValue === 'PROBABLE') {
      return PEPMatchResolution.PROBABLE;
    }
    return PEPMatchResolution.FALSE;
  }

  convertToBadPressMatchResolution(value: string): BadPressMatchResolution {
    if (!value) {
      return BadPressMatchResolution.FALSE;
    }
    const upperValue = value.toUpperCase();
    if (upperValue === 'TRUE') {
      return BadPressMatchResolution.TRUE;
    } else if (upperValue === 'PROBABLE') {
      return BadPressMatchResolution.PROBABLE;
    }
    return BadPressMatchResolution.FALSE;
  }

  onClientSelected(client: GenericSearchDetails) {
    this.selectClient(client, true);
  }

  onClientSelectionFromSearch(searchDetails: GenericSearchDetails) {
    this.selectClient(searchDetails, true);
  }

  selectClient(client: GenericSearchDetails, userInitiated: boolean = false) {
    if (!client || !client.entityId) {
      return;
    }

    const clientChanged = !this.selectedClient || this.selectedClient.clientId !== client.clientId;
    this.selectedClient = client;
    this.bestMatchClient = client;

    if (userInitiated && clientChanged) {
      this.accountManuallySelected = false;
      this.selectedAccount = null;
      this.matchedAccount = null;
      if (!this.accountsFoundByNumber) {
        this.matchedBankAccountId = null;
      }
    }

    this.changeDetectorRef.detectChanges();
    this.loadClientAccounts();
  }

  proceedToAccountSelection() {
    // This method is kept for backward compatibility but step-based flow is no longer used
    if (!this.selectedClient || !this.selectedClient.entityId) {
      this.snackBarService.showSimpleMsj('Please select a client from the search results');
      return;
    }
    
    this.loadClientAccounts();
  }

  loadClientAccounts() {
    if (!this.selectedClient || !this.selectedClient.entityId) {
      // If no client selected but we have a selected account, keep it
      console.log('loadClientAccounts: No client selected, returning. selectedClient:', this.selectedClient);
      return;
    }
    
    console.log('loadClientAccounts: Loading accounts for client:', this.selectedClient.clientId, this.selectedClient.clientName);
    
    this.loadingAccounts = true;
    this.clientAccounts = [];
    
    // Preserve the currently selected account
    const previouslySelectedAccount = this.selectedAccount;
    const preservedMatchedAccountId = this.matchedBankAccountId;
    const manualSelection = this.accountManuallySelected;
    const preferredAccountId = manualSelection && previouslySelectedAccount && previouslySelectedAccount.accountId
      ? previouslySelectedAccount.accountId
      : preservedMatchedAccountId;
    
    this.financeService.getBankInstitutionsAccountsForEntity(this.selectedClient.entityId).subscribe(
      (bankInstitutions: any) => {
        this.loadingAccounts = false;
        const accountList = this.flattenBankAccounts(bankInstitutions);
        this.clientAccounts = accountList;

        if (preferredAccountId) {
          const match = accountList.find(acc => acc.accountId === preferredAccountId);
          if (match && match.accountId) {
            this.matchedAccount = match;
            this.selectedAccount = match;
            this.matchedBankAccountId = match.accountId;
            this.changeDetectorRef.detectChanges();
            return;
          }
        }

        if (manualSelection) {
          this.changeDetectorRef.detectChanges();
          return;
        }
        
        // Auto-match by account number (and statement currency when multiple accounts share the number)
        if (this.extractedAccountNumber && accountList.length > 0) {
          const numberMatches = accountList.filter(
            acc => acc.accountNumber &&
            this.accountNumbersMatch(acc.accountNumber, this.extractedAccountNumber)
          );
          
          const picked = this.pickAccountByCurrency(numberMatches);
          if (picked && picked.accountId) {
            this.matchedAccount = picked;
            this.selectedAccount = picked;
            this.matchedBankAccountId = picked.accountId;
            this.changeDetectorRef.detectChanges();
          }
        }
        
        this.changeDetectorRef.detectChanges();
      },
      (error: any) => {
        this.loadingAccounts = false;
        console.error('Error loading client accounts: ' + error.message);
        this.snackBarService.showSimpleMsj('Error loading accounts: ' + (error.message || 'Unknown error'));
      }
    );
  }

  private flattenBankAccounts(bankInstitutions: any): BankAccount[] {
    const institutions = Array.isArray(bankInstitutions)
      ? bankInstitutions
      : (bankInstitutions && bankInstitutions.arrResult ? bankInstitutions.arrResult : []);

    const accounts: BankAccount[] = [];
    institutions.forEach((institution: any) => {
      if (institution.bankAccounts && Array.isArray(institution.bankAccounts)) {
        institution.bankAccounts.forEach((account: any) => {
          if (account && account.accountId) {
            accounts.push(account);
          }
        });
      }
    });
    return accounts;
  }

  private normalizeAccountNumber(accountNumber: string): string {
    if (!accountNumber) {
      return '';
    }
    return accountNumber.trim().toLowerCase().replace(/[\s/.\-]/g, '');
  }

  private extractDomesticAccountSuffix(accountNumber: string): string {
    if (!accountNumber) {
      return '';
    }
    const slashIndex = accountNumber.lastIndexOf('/');
    if (slashIndex >= 0 && slashIndex < accountNumber.length - 1) {
      return accountNumber.substring(slashIndex + 1).trim();
    }
    return '';
  }

  private accountNumbersMatch(storedNumber: string, searchNumber: string): boolean {
    if (!storedNumber || !searchNumber) {
      return false;
    }
    if (this.normalizeAccountNumber(storedNumber) === this.normalizeAccountNumber(searchNumber)) {
      return true;
    }
    const suffix = this.extractDomesticAccountSuffix(searchNumber);
    return suffix.length >= 6
      && this.normalizeAccountNumber(storedNumber) === this.normalizeAccountNumber(suffix);
  }

  isAccountSelected(account: BankAccount): boolean {
    return this.selectedAccount != null
      && account != null
      && this.selectedAccount.accountId === account.accountId;
  }

  isAccountMatched(account: BankAccount): boolean {
    return this.matchedAccount != null
      && account != null
      && this.matchedAccount.accountId === account.accountId;
  }

  isAccountCurrencySuggested(account: BankAccount): boolean {
    return this.extractedCurrency != null
      && this.extractedCurrency.trim().length > 0
      && account != null
      && this.currenciesMatch(account);
  }

  private normalizeCurrency(currency: string): string {
    if (!currency) {
      return '';
    }
    return currency.trim().toUpperCase();
  }

  private getAccountCurrency(account: BankAccount): string {
    return this.normalizeCurrency(account.currencyValue || account.currency || '');
  }

  private currenciesMatch(account: BankAccount): boolean {
    const extracted = this.normalizeCurrency(this.extractedCurrency);
    if (!extracted) {
      return false;
    }
    const accountCurrency = this.getAccountCurrency(account);
    return accountCurrency !== '' && accountCurrency === extracted;
  }

  private pickAccountByCurrency(accounts: BankAccount[]): BankAccount | null {
    if (!accounts || accounts.length === 0) {
      return null;
    }
    const extracted = this.normalizeCurrency(this.extractedCurrency);
    if (!extracted) {
      return accounts.length === 1 ? accounts[0] : null;
    }
    const currencyMatches = accounts.filter(account => this.currenciesMatch(account));
    return currencyMatches.length === 1 ? currencyMatches[0] : null;
  }

  private applyExtractedCurrencyToCreateForm() {
    if (!this.extractedCurrency || !this.currencies || this.currencies.length === 0) {
      return;
    }
    const normalized = this.normalizeCurrency(this.extractedCurrency);
    const match = this.currencies.find(currency => this.normalizeCurrency(currency.value) === normalized);
    if (match) {
      this.createAccountForm.patchValue({ currencyId: match.id });
    }
  }

  private buildAccountFromHolder(holder: any, fallbackAccountNumber?: string): BankAccount {
    return {
      accountId: holder.bankAccountId,
      accountNumber: holder.accountNumber || fallbackAccountNumber || this.extractedAccountNumber,
      accountName: holder.accountName || ('Account ' + (holder.accountNumber || fallbackAccountNumber || '')),
      currencyValue: holder.currency || null
    } as BankAccount;
  }

  private buildAccountsFromHolders(holders: any[], fallbackAccountNumber?: string): BankAccount[] {
    const accountMap = new Map<number, BankAccount>();
    holders.forEach((holder: any) => {
      if (holder.bankAccountId && !accountMap.has(holder.bankAccountId)) {
        accountMap.set(holder.bankAccountId, this.buildAccountFromHolder(holder, fallbackAccountNumber));
      }
    });
    return Array.from(accountMap.values());
  }

  private clearGlobalAccountMatchState(clearSelectedAccount: boolean = false) {
    this.matchedBankAccountId = null;
    this.matchedAccount = null;
    this.accountsFoundByNumber = false;
    this.allAccounts = [];
    if (clearSelectedAccount && !this.accountManuallySelected) {
      this.selectedAccount = null;
    }
  }

  onAccountSelected(account: BankAccount, fromClientList: boolean = false) {
    this.selectedAccount = account;
    this.matchedAccount = account;
    this.matchedBankAccountId = account.accountId;
    this.accountManuallySelected = true;
    this.showCreateAccountForm = false;
    this.changeDetectorRef.detectChanges();

    if (fromClientList && this.selectedClient) {
      return;
    }

    this.loadAccountOwners(account, true);
  }

  onAccountSearchChange(value: string) {
    if (!value || value.trim().length < 2) {
      this.clearGlobalAccountMatchState(false);
      return;
    }
    
    this.searchAccounts(value);
  }

  searchAccounts(searchTerm: string) {
    if (!searchTerm || searchTerm.trim().length < 2) {
      this.clearGlobalAccountMatchState(false);
      return;
    }
    
    this.searchingAccounts = true;
    
    const payload = {
      accountNumber: searchTerm.trim()
    };
    
    this.requestService.postRequest(
      ConstantsService.BANK_STATEMENT_BATCH_FIND_ACCOUNT_HOLDERS,
      payload,
      new Headers()
    ).subscribe(
      (response: any) => {
        this.searchingAccounts = false;
        
        if (response && response.holders && Array.isArray(response.holders) && response.holders.length > 0) {
          this.allAccounts = this.buildAccountsFromHolders(response.holders, response.accountNumber);
          
          const uniqueBankAccountIds = new Set<number>();
          response.holders.forEach((holder: any) => {
            if (holder.bankAccountId) {
              uniqueBankAccountIds.add(holder.bankAccountId);
            }
          });

          if (uniqueBankAccountIds.size === 1 && !this.accountManuallySelected) {
            const account = this.allAccounts[0];
            if (account && account.accountId) {
              this.selectedAccount = account;
              this.matchedAccount = account;
              this.matchedBankAccountId = account.accountId;
              this.changeDetectorRef.detectChanges();
              this.loadAccountOwners(account, true);
            }
          } else if (uniqueBankAccountIds.size > 1 && !this.accountManuallySelected) {
            const currencyMatch = this.pickAccountByCurrency(this.allAccounts);
            if (currencyMatch && currencyMatch.accountId) {
              this.selectedAccount = currencyMatch;
              this.matchedAccount = currencyMatch;
              this.matchedBankAccountId = currencyMatch.accountId;
              this.changeDetectorRef.detectChanges();
              this.loadAccountOwners(currencyMatch, true);
            }
          }
        } else {
          this.allAccounts = [];
        }
      },
      (error: any) => {
        this.searchingAccounts = false;
        console.error('Error searching accounts: ' + error.message);
        this.allAccounts = [];
      }
    );
  }

  loadAccountOwners(account: BankAccount, autoSelectClient: boolean = true) {
    if (!account || !account.accountId) {
      this.accountOwners = [];
      return;
    }

    const payload: any = {
      bankAccountId: account.accountId
    };
    if (account.accountNumber || this.extractedAccountNumber) {
      payload.accountNumber = account.accountNumber || this.extractedAccountNumber;
    }
    
    this.requestService.postRequest(
      ConstantsService.BANK_STATEMENT_BATCH_FIND_ACCOUNT_HOLDERS,
      payload,
      new Headers()
    ).subscribe(
      (response: any) => {
        if (response && response.holders && Array.isArray(response.holders)) {
          if (this.selectedAccount && !this.selectedAccount.currencyValue && response.holders.length > 0
              && response.holders[0] && response.holders[0].currency) {
            this.selectedAccount.currencyValue = response.holders[0].currency;
          }
          this.accountOwners = response.holders.map((holder: any) => {
            const clientDetails = new GenericSearchDetails();
            clientDetails.id = holder.entityId;
            clientDetails.clientId = holder.clientId;
            clientDetails.entityId = holder.entityId;
            clientDetails.clientName = holder.clientName || 'Unknown';
            clientDetails.clientType = holder.clientType || 'Unknown';
            clientDetails.clientGender = null;
            clientDetails.clientLogo = null;
            clientDetails.entityType = holder.clientType || 'Unknown';
            clientDetails.sanctionStatus = SanctionMatchResolution.FALSE;
            clientDetails.pepStatus = PEPMatchResolution.FALSE;
            clientDetails.badPressStatus = BadPressMatchResolution.FALSE;
            return clientDetails;
          });
          
          if (autoSelectClient && !this.accountManuallySelected) {
            if (this.accountOwners.length === 1) {
              const client = this.accountOwners[0];
              if (client && client.clientId && client.entityId) {
                if (this.clientSearchResults.indexOf(client) === -1) {
                  this.clientSearchResults = [client];
                }
                this.selectClient(client, false);
              }
            } else if (this.accountOwners.length > 1) {
              this.clientSearchResults = this.accountOwners;
              this.bestMatchClient = this.accountOwners[0];
            }
          } else if (autoSelectClient && this.accountManuallySelected && !this.selectedClient
              && this.accountOwners.length === 1) {
            const client = this.accountOwners[0];
            if (client && client.clientId && client.entityId) {
              this.selectClient(client, false);
            }
          }
        } else {
          this.accountOwners = [];
        }
      },
      (error: any) => {
        console.error('Error loading account owners: ' + error.message);
        this.accountOwners = [];
      }
    );
  }

  showCreateAccount() {
    this.showCreateAccountForm = true;
    this.selectedAccount = null;
    this.matchedAccount = null;
    this.accountManuallySelected = false;

    if (this.selectedClient) {
      this.createAccountForm.patchValue({
        accountName: this.selectedClient.clientName
      });
    }
    this.applyExtractedCurrencyToCreateForm();
  }

  backToClientSelection() {
    // This method is kept for backward compatibility
    this.showCreateAccountForm = false;
    this.selectedAccount = null;
  }

  loadBankInstitutions() {
    this.financeService.getBankInstitutionsList().subscribe(
      (institutions: any) => {
        this.bankInstitutions = Array.isArray(institutions) ? institutions : [];
      },
      (error: any) => {
        console.error('Error loading bank institutions: ' + error.message);
      }
    );
  }

  loadCurrencies() {
    const currencyValues = this.warmUpService.getApplicationValue(ApplicationTypeConstants.CURRENCY);
    if (currencyValues instanceof Observable) {
      currencyValues.subscribe(
        (response: any) => {
          this.warmUpService.setApplicationTypeInLS(response);
          this.currencies = response.values;
          this.applyExtractedCurrencyToCreateForm();
        }
      );
    } else if (currencyValues && currencyValues.length > 0) {
      this.currencies = currencyValues;
      this.applyExtractedCurrencyToCreateForm();
    } else {
      this.requestService.getRequest('/application-values/Currency').subscribe(
        (response: any) => {
          if (response && response.arrResult) {
            this.currencies = response.arrResult;
          } else if (Array.isArray(response)) {
            this.currencies = response;
          }
          this.applyExtractedCurrencyToCreateForm();
        },
        (error: any) => {
          console.error('Error loading currencies: ' + error.message);
          this.currencies = [{ id: 1, value: 'EUR', description: 'Euro' }];
        }
      );
    }
  }

  onCancel() {
    this.dialogRef.close();
  }

  onMatchAccount() {
    if (this.showCreateAccountForm) {
      // Create new account
      this.onCreateAccount();
    } else if (this.selectedAccount) {
      // Match to existing account
      this.matchToAccount();
    } else {
      this.snackBarService.showSimpleMsj('Please select an account or create a new one');
    }
  }

  canMatch(): boolean {
    // Can match if we have both client and account, or if creating new account
    if (this.showCreateAccountForm) {
      return this.selectedClient != null && this.createAccountForm.valid;
    }
    
    // Check that both client and account are selected and have required IDs
    const hasClient = this.selectedClient != null && this.selectedClient.clientId != null;
    const hasAccount = this.selectedAccount != null && this.selectedAccount.accountId != null;
    const canMatchResult = hasClient && hasAccount;
    
    // Debug log - only log once per second to avoid spam
    if (!canMatchResult && (!this.lastCanMatchLog || Date.now() - this.lastCanMatchLog > 1000)) {
      console.log('canMatch() returned false - hasClient:', hasClient, 'hasAccount:', hasAccount, 
                  'selectedClient:', this.selectedClient, 'selectedAccount:', this.selectedAccount);
      // If we have account but no client, log a warning
      if (hasAccount && !hasClient) {
        console.warn('WARNING: Account is selected but client is not! This should not happen. Account:', this.selectedAccount);
      }
      this.lastCanMatchLog = Date.now();
    }
    
    return canMatchResult;
  }
  
  private lastCanMatchLog: number = 0;

  matchToAccount() {
    if (!this.selectedAccount || !this.bankStatementId) {
      this.snackBarService.showSimpleMsj('Please select an account');
      return;
    }
    
    if (!this.selectedClient || !this.selectedClient.clientId) {
      this.snackBarService.showSimpleMsj('Please select a client');
      return;
    }
    
    if (!this.selectedAccount.accountId) {
      this.snackBarService.showSimpleMsj('Selected account is missing account ID. Please select the account again.');
      return;
    }
    
    const payload = {
      bankStatementId: this.bankStatementId,
      clientId: this.selectedClient.clientId,
      bankAccountId: this.selectedAccount.accountId
    };
    
    this.requestService.postRequest(
      ConstantsService.BANK_STATEMENT_BATCH_MATCH,
      payload,
      new Headers()
    ).subscribe(
      (response: any) => {
        this.dialogRef.close({
          success: true,
          matched: true,
          clientId: this.selectedClient.clientId,
          bankAccountId: this.selectedAccount.accountId
        });
      },
      (error: any) => {
        this.snackBarService.showSimpleMsj('Error matching account: ' + (error.message || 'Unknown error'));
      }
    );
  }

  onCreateAccount() {
    if (!this.selectedClient || !this.selectedClient.entityId) {
      this.snackBarService.showSimpleMsj('Please select a valid client');
      return;
    }

    if (this.createAccountForm.valid) {
      this.creatingAccount = true;
      const formValue = this.createAccountForm.getRawValue();
      
      const selectedBank = this.bankInstitutions.find(b => 
        (b.bankId && b.bankId === formValue.bankInstitutionId) || 
        (b.id && b.id === formValue.bankInstitutionId)
      );
      
      const accountData = {
        accountName: formValue.accountName,
        accountNumber: formValue.accountNumber,
        bankInstitutionId: selectedBank ? (selectedBank.bankId || selectedBank.id) : formValue.bankInstitutionId,
        bankInstitutionName: selectedBank ? selectedBank.bankName : '',
        currencyId: formValue.currencyId,
        accountHolderEntityId: this.selectedClient.entityId
      };

      this.financeService.saveBankAccount(accountData).subscribe(
        (response: any) => {
          this.creatingAccount = false;
          if (response && response.flagResult) {
            let bankAccountId = null;
            if (response.bankAccountId) {
              bankAccountId = response.bankAccountId;
            } else if (response.arrResult && response.arrResult.length > 0 && response.arrResult[0].accountId) {
              bankAccountId = response.arrResult[0].accountId;
            } else if (response.accountId) {
              bankAccountId = response.accountId;
            }
            
            // After creating account, match it to the bank statement
            if (bankAccountId && this.bankStatementId) {
              const matchPayload = {
                bankStatementId: this.bankStatementId,
                clientId: this.selectedClient.clientId,
                bankAccountId: bankAccountId
              };
              
              this.requestService.postRequest(
                ConstantsService.BANK_STATEMENT_BATCH_MATCH,
                matchPayload,
                new Headers()
              ).subscribe(
                (matchResponse: any) => {
                  this.dialogRef.close({
                    success: true,
                    matched: true,
                    created: true,
                    clientId: this.selectedClient.clientId,
                    bankAccountId: bankAccountId
                  });
                },
                (matchError: any) => {
                  this.snackBarService.showSimpleMsj('Account created but error matching to bank statement: ' + (matchError.message || 'Unknown error'));
                  this.dialogRef.close({
                    success: true,
                    matched: false,
                    created: true,
                    clientId: this.selectedClient.clientId,
                    bankAccountId: bankAccountId
                  });
                }
              );
            } else {
              this.dialogRef.close({
                success: true,
                matched: false,
                created: true,
                clientId: this.selectedClient.clientId,
                bankAccountId: bankAccountId
              });
            }
          } else {
            this.snackBarService.showSimpleMsj('Account created but could not determine account ID');
            this.dialogRef.close({
              success: true,
              created: true,
              clientId: this.selectedClient.clientId,
              bankAccountId: null
            });
          }
        },
        (error: any) => {
          this.creatingAccount = false;
          this.snackBarService.showSimpleMsj('Error creating account: ' + (error.message || 'Unknown error'));
        }
      );
    } else {
      Object.keys(this.createAccountForm.controls).forEach(key => {
        const control = this.createAccountForm.get(key);
        if (control) {
          control.markAsTouched();
        }
      });
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/finance/bank-statements-batch/find-account-dialog/find-account-dialog.component.ts